/**
 * Chat replies in blocks: runs of markdown pipe-table rows become one table, every other line
 * stays a line. The web and mobile chat renderers both draw from this, so a table parses the
 * same way on each.
 */

export type ChatMarkdownBlock =
  | { kind: 'line'; text: string }
  | { kind: 'table'; header: string[] | null; rows: string[][] };

function isTableSeparator(line: string): boolean {
  // The whole line is dashes, colons and pipes, so a row whose first cell is "-3.5" is data.
  return /^\|(\s*:?-+:?\s*\|)+$/.test(line.trim());
}

function isTableRow(line: string): boolean {
  return /^\|(.+)\|$/.test(line.trim());
}

function cellsOf(line: string): string[] {
  return line
    .trim()
    .slice(1, -1)
    .split('|')
    .map((cell) => cell.trim());
}

export function splitChatMarkdown(text: string): ChatMarkdownBlock[] {
  const blocks: ChatMarkdownBlock[] = [];
  let tableLines: string[] = [];

  const flushTable = (): void => {
    if (tableLines.length === 0) return;
    const hasHeader = tableLines.length > 1 && isTableSeparator(tableLines[1]);
    const dataLines = tableLines.filter((line) => !isTableSeparator(line));
    blocks.push({
      kind: 'table',
      header: hasHeader ? cellsOf(dataLines[0]) : null,
      rows: (hasHeader ? dataLines.slice(1) : dataLines).map(cellsOf),
    });
    tableLines = [];
  };

  for (const line of text.split('\n')) {
    if (isTableRow(line) || isTableSeparator(line)) {
      tableLines.push(line);
      continue;
    }
    flushTable();
    blocks.push({ kind: 'line', text: line });
  }
  flushTable();
  return blocks;
}
